// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-18
// protected: NO
// =============================================================================
// Dashboard Monitoring V2: (1) Ledger Health Check, (2) Failed Transaction Log.
// Membaca /api/v2/monitoring/* (RLS region-scoped). Akses: OWNER & Kepala Cabang.
import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Activity, AlertTriangle, CheckCircle2, RefreshCw, ShieldCheck, ScanLine, Loader2,
  Inbox, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { TableScroll } from "@/v2/components/TableScroll";
import { EmptyState, TableSkeletonRows, useDelayedLoading } from "@/v2/components/ux";

const API = process.env.REACT_APP_BACKEND_URL;
const MODULES = [
  "warehouse_receive", "warehouse_compliment", "transfer_ship", "transfer_receive",
  "shipment_ship", "shipment_receive", "return_ship", "return_receive",
];

const StatCard = ({ icon: Icon, label, value, tone = "default", testid }) => (
  <Card className="p-4 rounded-2xl" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-xl ${tone === "danger" ? "bg-destructive/10 text-destructive"
        : tone === "ok" ? "bg-[hsl(var(--badge-success))]/10 text-[hsl(var(--badge-success))]" : "bg-primary/10 text-primary"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
      </div>
    </div>
  </Card>
);

export const V2Monitoring = ({ authHeader, role }) => {
  const [health, setHealth] = useState(null);
  const [failed, setFailed] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [region, setRegion] = useState("ALL");
  const [moduleF, setModuleF] = useState("ALL");
  // FASE 3B.1 — Audit View
  const [audit, setAudit] = useState({ items: [], total: 0, page: 1, total_pages: 1, region_scope: "" });
  const [aFrom, setAFrom] = useState("");
  const [aTo, setATo] = useState("");
  const [actor, setActor] = useState("");
  const [aStatus, setAStatus] = useState("ALL");
  const [aPage, setAPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const [detail, setDetail] = useState(null);  // Tahap 3 UX: detail baris (failed/audit) read-only
  const isOwner = role === "OWNER";

  const loadHealth = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/v2/monitoring/ledger-health`, { headers: authHeader() });
      if (r.ok) setHealth(await r.json());
    } catch (e) { toast.error("Gagal memuat health: " + e.message); }
  }, [authHeader]);

  const loadFailed = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      if (region !== "ALL") p.set("region", region);
      if (moduleF !== "ALL") p.set("module", moduleF);
      const r = await fetch(`${API}/api/v2/monitoring/failed-transactions?${p.toString()}`,
        { headers: authHeader() });
      if (r.ok) setFailed(await r.json());
    } catch (e) { toast.error("Gagal memuat failed log: " + e.message); }
    finally { setLoading(false); }
  }, [authHeader, dateFrom, dateTo, region, moduleF]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const p = new URLSearchParams();
      if (aFrom) p.set("date_from", aFrom);
      if (aTo) p.set("date_to", aTo);
      if (actor) p.set("actor", actor);
      if (aStatus !== "ALL") p.set("status", aStatus);
      p.set("page", String(aPage));
      p.set("page_size", "20");
      const r = await fetch(`${API}/api/v2/monitoring/audit?${p.toString()}`, { headers: authHeader() });
      if (r.ok) setAudit(await r.json());
    } catch (e) { toast.error("Gagal memuat audit: " + e.message); }
    finally { setAuditLoading(false); }
  }, [authHeader, aFrom, aTo, actor, aStatus, aPage]);

  useEffect(() => { loadHealth(); loadFailed(); }, [loadHealth, loadFailed]);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const showFailedSkeleton = useDelayedLoading(loading && failed.items.length === 0);
  const showAuditSkeleton = useDelayedLoading(auditLoading && audit.items.length === 0);

  const integrity = health?.ledger_integrity?.status;
  const last = health?.last_successful_transaction;

  return (
    <div className="space-y-5" data-testid="v2-monitoring">
      {/* ---- Ledger Health Check ---- */}
      <Card className="p-5 rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Ledger Health Check</h3>
            <Badge variant="outline">scope {health?.region_scope || "-"}</Badge>
          </div>
          <Button size="sm" variant="outline" onClick={loadHealth} data-testid="v2-mon-refresh-health">
            <RefreshCw className="w-4 h-4 mr-1" />Segarkan
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <StatCard icon={ScanLine} label="Movement hari ini" value={health?.movements_today ?? "—"} testid="v2-mon-movements" />
          <StatCard icon={CheckCircle2} label="Sukses hari ini" value={health?.success_today ?? "—"} tone="ok" testid="v2-mon-success" />
          <StatCard icon={AlertTriangle} label="Gagal hari ini" value={health?.failed_today ?? "—"} tone="danger" testid="v2-mon-failed" />
          <StatCard icon={AlertTriangle} label="Double-scan ditolak" value={health?.double_scan_rejected ?? "—"} tone="danger" testid="v2-mon-double" />
          <StatCard icon={AlertTriangle} label="Pelanggaran state-machine" value={health?.state_machine_violations ?? "—"} tone="danger" testid="v2-mon-violation" />
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <Card className="p-4 rounded-2xl" data-testid="v2-mon-integrity">
            <div className="flex items-center gap-3">
              <ShieldCheck className={`w-5 h-5 ${integrity === "HEALTHY" ? "text-[hsl(var(--badge-success))]" : "text-destructive"}`} />
              <div>
                <p className="text-xs text-muted-foreground">Status Integritas Ledger</p>
                <p className="font-semibold">
                  <Badge variant={integrity === "HEALTHY" ? "default" : "destructive"}>{integrity || "—"}</Badge>
                  <span className="ml-2 text-sm text-muted-foreground">
                    cache mismatch: {health?.ledger_integrity?.cache_mismatch ?? "—"}
                  </span>
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-2xl" data-testid="v2-mon-last">
            <p className="text-xs text-muted-foreground mb-1">Transaksi sukses terakhir</p>
            {last ? (
              <p className="text-sm">
                <Badge variant="outline" className="mr-1">{last.movement_type}</Badge>
                <span className="font-mono text-xs">{last.qr_id || last.ref_no || "-"}</span>
                <span className="text-muted-foreground"> · {last.module} · {last.actor}</span>
                <br /><span className="text-xs text-muted-foreground">{last.occurred_at}</span>
              </p>
            ) : <p className="text-sm text-muted-foreground">Belum ada.</p>}
          </Card>
        </div>
      </Card>

      {/* ---- Failed Transaction Log ---- */}
      <Card className="p-5 rounded-2xl">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <h3 className="font-semibold">Failed Transaction Log</h3>
            <Badge variant="secondary" data-testid="v2-mon-failed-count">{failed.count} entri</Badge>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="text-xs">Dari</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="h-9" data-testid="v2-mon-date-from" />
            </div>
            <div>
              <Label className="text-xs">Sampai</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="h-9" data-testid="v2-mon-date-to" />
            </div>
            {isOwner && (
              <div>
                <Label className="text-xs">Region</Label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger className="h-9 w-28" data-testid="v2-mon-region"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Semua</SelectItem>
                    <SelectItem value="SBY">SBY</SelectItem>
                    <SelectItem value="SMD">SMD</SelectItem>
                    <SelectItem value="GLOBAL">GLOBAL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Modul</Label>
              <Select value={moduleF} onValueChange={setModuleF}>
                <SelectTrigger className="h-9 w-44" data-testid="v2-mon-module"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Semua modul</SelectItem>
                  {MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="outline" onClick={loadFailed} data-testid="v2-mon-apply">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <TableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead><TableHead>User</TableHead><TableHead>Region</TableHead>
                <TableHead>Modul</TableHead><TableHead>QR</TableHead><TableHead>Error</TableHead>
                <TableHead>Detail</TableHead><TableHead>Retry</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showFailedSkeleton && failed.items.length === 0 && <TableSkeletonRows rows={4} cols={8} testid="v2-mon-failed-skeleton" />}
              {!showFailedSkeleton && failed.items.length === 0 && (
                <TableRow><TableCell colSpan={8} className="p-0">
                  <EmptyState icon={CheckCircle2} title="Tidak ada transaksi gagal"
                    description="Bagus — tidak ada kegagalan pada rentang tanggal & filter ini. Ubah filter atau perluas rentang tanggal untuk memeriksa periode lain."
                    testid="v2-mon-failed-empty" />
                </TableCell></TableRow>
              )}
              {failed.items.map((x, i) => (
                <TableRow key={i} data-testid="v2-mon-failed-row"
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setDetail({ title: "Detail Transaksi Gagal", rows: [
                    ["Waktu", x.timestamp ? new Date(x.timestamp).toLocaleString("id-ID") : "-"],
                    ["User", x.user], ["Role", x.role], ["Region", x.region || "-"],
                    ["Modul", x.module], ["QR", x.qr_id || "-"],
                    ["Kode Error", x.error_code], ["Detail Error", x.error_detail || "-"],
                    ["Retry", x.retry_status === "RETRIED_OK" ? "Sudah retry" : "Belum"],
                  ] })}
                  tabIndex={0} role="button"
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.click(); } }}>
                  <TableCell className="text-xs whitespace-nowrap">
                    {x.timestamp ? new Date(x.timestamp).toLocaleString("id-ID") : "-"}
                  </TableCell>
                  <TableCell className="text-xs">{x.user}<br /><span className="text-muted-foreground">{x.role}</span></TableCell>
                  <TableCell><Badge variant="outline">{x.region || "-"}</Badge></TableCell>
                  <TableCell className="text-xs">{x.module}</TableCell>
                  <TableCell className="font-mono text-xs">{x.qr_id || "-"}</TableCell>
                  <TableCell><Badge variant="destructive">{x.error_code}</Badge></TableCell>
                  <TableCell className="text-xs max-w-xs truncate" title={x.error_detail}>{x.error_detail}</TableCell>
                  <TableCell>
                    <Badge variant={x.retry_status === "RETRIED_OK" ? "default" : "secondary"}>
                      {x.retry_status === "RETRIED_OK" ? "Sudah retry" : "Belum"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
      </Card>

      {/* ---- FASE 3B.1: Audit View (read-only, region-aware) ---- */}
      <Card className="p-5 rounded-2xl" data-testid="v2-audit-card">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Audit Trail</h3>
          <Badge variant="outline" className="ml-1">Region: {audit.region_scope || "-"}</Badge>
          <Badge variant="secondary" className="ml-auto">{audit.total} kejadian</Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div><Label className="text-xs">Dari</Label>
            <Input type="date" value={aFrom} onChange={(e) => { setAPage(1); setAFrom(e.target.value); }}
                   data-testid="v2-audit-from" /></div>
          <div><Label className="text-xs">Sampai</Label>
            <Input type="date" value={aTo} onChange={(e) => { setAPage(1); setATo(e.target.value); }}
                   data-testid="v2-audit-to" /></div>
          <div><Label className="text-xs">Aktor (email/role)</Label>
            <Input value={actor} placeholder="cari..." onChange={(e) => { setAPage(1); setActor(e.target.value); }}
                   data-testid="v2-audit-actor" /></div>
          <div><Label className="text-xs">Status</Label>
            <Select value={aStatus} onValueChange={(v) => { setAPage(1); setAStatus(v); }}>
              <SelectTrigger data-testid="v2-audit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Semua</SelectItem>
                <SelectItem value="SUCCESS">SUCCESS</SelectItem>
                <SelectItem value="FAILED">FAILED</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="flex items-end">
            <Button size="sm" variant="outline" onClick={loadAudit} data-testid="v2-audit-apply">
              <RefreshCw className="w-4 h-4 mr-1" />Terapkan
            </Button></div>
        </div>
        <TableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead><TableHead>Aktor</TableHead><TableHead>Region</TableHead>
                <TableHead>Modul</TableHead><TableHead>Aksi</TableHead><TableHead>Entitas</TableHead>
                <TableHead>Status</TableHead><TableHead>Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showAuditSkeleton && audit.items.length === 0 && <TableSkeletonRows rows={4} cols={8} testid="v2-audit-skeleton" />}
              {!showAuditSkeleton && audit.items.length === 0 && (
                <TableRow><TableCell colSpan={8} className="p-0">
                  <EmptyState icon={Inbox} title="Belum ada kejadian audit"
                    description="Tidak ada aktivitas tercatat untuk filter ini. Ubah rentang tanggal, aktor, atau status untuk melihat riwayat kejadian lainnya."
                    testid="v2-audit-empty" />
                </TableCell></TableRow>
              )}
              {audit.items.map((x, i) => (
                <TableRow key={i} data-testid="v2-audit-row"
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setDetail({ title: "Detail Kejadian Audit", rows: [
                    ["Waktu", x.timestamp ? new Date(x.timestamp).toLocaleString("id-ID") : "-"],
                    ["Aktor", x.actor], ["Role Aktor", x.actor_role], ["Region", x.region || "-"],
                    ["Modul", x.module], ["Aksi", x.action || "-"],
                    ["Entitas", `${x.entity || "-"}${x.ref_no ? ` · ${x.ref_no}` : ""}`],
                    ["Status", x.status], ["QR", x.qr_id || "-"], ["Detail", x.error_detail || "-"],
                  ] })}
                  tabIndex={0} role="button"
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.click(); } }}>
                  <TableCell className="text-xs whitespace-nowrap">
                    {x.timestamp ? new Date(x.timestamp).toLocaleString("id-ID") : "-"}</TableCell>
                  <TableCell className="text-xs">{x.actor}<br />
                    <span className="text-muted-foreground">{x.actor_role}</span></TableCell>
                  <TableCell><Badge variant="outline">{x.region || "-"}</Badge></TableCell>
                  <TableCell className="text-xs">{x.module}</TableCell>
                  <TableCell className="text-xs">{x.action || "-"}</TableCell>
                  <TableCell className="text-xs">{x.entity || "-"}{x.ref_no ? ` · ${x.ref_no}` : ""}</TableCell>
                  <TableCell>
                    <Badge variant={x.status === "FAILED" ? "destructive" : "secondary"}>{x.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs max-w-xs truncate" title={x.error_detail || ""}>
                    {x.error_detail || (x.qr_id ? x.qr_id : "-")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-muted-foreground">
            Halaman {audit.page} / {audit.total_pages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={audit.page <= 1}
                    onClick={() => setAPage((p) => Math.max(1, p - 1))} data-testid="v2-audit-prev">
              Sebelumnya</Button>
            <Button size="sm" variant="outline" disabled={audit.page >= audit.total_pages}
                    onClick={() => setAPage((p) => p + 1)} data-testid="v2-audit-next">
              Berikutnya</Button>
          </div>
        </div>
      </Card>

      {/* Tahap 3 UX: Detail baris (Failed / Audit) read-only via klik baris. */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="v2-mon-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />{detail?.title}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-1.5 text-sm max-h-[60vh] overflow-auto">
              {detail.rows.map(([k, v], i) => (
                <div key={i} className="flex items-start justify-between gap-4 border-b last:border-0 py-1.5">
                  <span className="text-xs text-muted-foreground shrink-0">{k}</span>
                  <span className="text-right break-words font-medium">{String(v ?? "-")}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
